import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, ArrowLeft, Phone, User } from 'lucide-react';
import { api } from '../../utils/api';

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function WhatsAppConversations() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  const loadConvos = async () => {
    try {
      const data = await api.get('/api/whatsapp/conversations');
      setConvos(Array.isArray(data) ? data : []);
    } catch { setConvos([]); }
  };

  const openThread = async (phone) => {
    setSelected(phone);
    setLoadingThread(true);
    try {
      const data = await api.get(`/api/whatsapp/conversations/${phone}`);
      setThread(Array.isArray(data) ? data : []);
      // refresh convos to clear unread badge
      loadConvos();
    } catch { setThread([]); }
    setLoadingThread(false);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    try {
      await api.post('/api/whatsapp/reply', { to: selected, message: reply.trim() });
      setReply('');
      await openThread(selected);
    } catch (e) {
      alert(e.message || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden">
      {/* Sidebar — conversation list */}
      <div className={`${selected ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r bg-white`}>
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm" style={{ background: '#25D366' }}>
              <MessageCircle size={16} />
            </div>
            <h2 className="font-bold text-gray-800">Conversations</h2>
          </div>
          <p className="text-xs text-gray-500">{convos.length} threads</p>
        </div>
        {convos.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm p-6 text-center">
            No conversations yet.<br />Messages from parents will appear here.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto divide-y">
            {convos.map(c => (
              <button key={c.from_number} onClick={() => openThread(c.from_number)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selected === c.from_number ? 'bg-green-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ background: (c.unread_count > 0) ? '#25D366' : '#9ca3af' }}>
                    {(c.contact_name || c.from_number || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <span className="font-semibold text-sm text-gray-800 truncate">{c.contact_name || 'Unknown'}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{fmtTime(c.wa_timestamp)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{c.from_number}</p>
                    <p className="text-xs text-gray-600 truncate mt-0.5">{c.body}</p>
                  </div>
                  {c.unread_count > 0 && (
                    <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0"
                      style={{ background: '#25D366' }}>{c.unread_count}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Thread panel */}
      {selected ? (
        <div className="flex-1 flex flex-col bg-gray-50">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 bg-white border-b">
            <button onClick={() => setSelected(null)} className="md:hidden text-gray-500 hover:text-gray-700">
              <ArrowLeft size={20} />
            </button>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: '#25D366' }}>
              {(convos.find(c => c.from_number === selected)?.contact_name || selected)[0].toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-800">
                {convos.find(c => c.from_number === selected)?.contact_name || 'Unknown'}
              </p>
              <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{selected}</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loadingThread ? (
              <div className="text-center text-gray-400 py-8 text-sm">Loading…</div>
            ) : thread.map(msg => (
              <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl text-sm ${
                  msg.direction === 'outbound'
                    ? 'text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'}`}
                  style={{ background: msg.direction === 'outbound' ? '#25D366' : undefined }}>
                  <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                  <p className={`text-xs mt-1 ${msg.direction === 'outbound' ? 'text-green-100' : 'text-gray-400'} text-right`}>
                    {fmtTime(msg.wa_timestamp)}
                    {msg.direction === 'outbound' && (
                      <span className="ml-1">{msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Reply box */}
          <div className="p-4 bg-white border-t flex items-end gap-3">
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              placeholder="Type a message… (Enter to send)"
              rows={2}
              className="flex-1 border rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300 resize-none" />
            <button onClick={sendReply} disabled={!reply.trim() || sending}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40"
              style={{ background: '#25D366' }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
          <div className="text-center text-gray-400">
            <User size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select a conversation to view the thread</p>
          </div>
        </div>
      )}
    </div>
  );
}
